/**
 * LocalStorage utility functions
 * Use localStorage for large data that doesn't need to be sent with every HTTP request
 * 
 * Features:
 * - Compression support for large data
 * - Graceful error handling
 * - Storage availability checks (cached)
 * - Size monitoring
 * - Optimized JSON compression
 */

// Cache storage availability to avoid repeated checks
let _storageAvailable = null;

// Check if localStorage is available (cached result)
const isStorageAvailable = () => {
    if (_storageAvailable !== null) {
        return _storageAvailable;
    }
    
    try {
        const test = '__storage_test__';
        localStorage.setItem(test, test);
        localStorage.removeItem(test);
        _storageAvailable = true;
        return true;
    } catch (e) {
        _storageAvailable = false;
        return false;
    }
};

// Check if string is likely JSON
const isJSONString = (str) => {
    if (typeof str !== 'string' || str.length === 0) return false;
    const trimmed = str.trim();
    return (trimmed.startsWith('{') && trimmed.endsWith('}')) || 
           (trimmed.startsWith('[') && trimmed.endsWith(']'));
};

// Optimized compression: remove whitespace from JSON
// This can reduce JSON size by 30-50% without external libraries
const compressJSON = (jsonString) => {
    // Quick check if it's JSON before attempting parse
    if (!isJSONString(jsonString)) {
        return jsonString;
    }
    
    try {
        // Parse and stringify to remove whitespace
        const parsed = JSON.parse(jsonString);
        return JSON.stringify(parsed);
    } catch (e) {
        // If not valid JSON, return as-is
        return jsonString;
    }
};

// Decompress (no-op since we only removed whitespace)
const decompressJSON = (compressedString) => {
    return compressedString;
};

// Get storage size in KB (optimized)
const getStorageSize = (name) => {
    if (!isStorageAvailable()) {
        return 0;
    }
    
    try {
        const item = localStorage.getItem(name);
        if (!item) return 0;
        // Use string length as approximation (faster than Blob for large strings)
        // Actual size may vary slightly due to encoding, but good enough for monitoring
        return (item.length * 2) / 1024; // Approximate: 2 bytes per char for UTF-16
    } catch (e) {
        return 0;
    }
};

export const setLocalStorage = (name, value, options = {}) => {
    const { compress = true, maxSizeKB = 100, silent = false } = options;
    
    if (!isStorageAvailable()) {
        if (!silent) {
            console.warn(`localStorage not available for "${name}"`);
        }
        return false;
    }
    
    if (value === null || value === undefined) {
        // Delete if value is null/undefined
        deleteLocalStorage(name);
        return true;
    }
    
    try {
        let valueToStore = value;
        
        // Compress if enabled and value is JSON string
        if (compress && typeof value === 'string' && value.length > 500 && isJSONString(value)) {
            const compressed = compressJSON(value);
            // Only use compressed if it's actually smaller
            if (compressed.length < value.length) {
                valueToStore = compressed;
            }
        }
        
        // Check size before storing
        const sizeKB = new Blob([valueToStore]).size / 1024;
        if (sizeKB > maxSizeKB && !silent) {
            console.warn(`Storage item "${name}" is ${sizeKB.toFixed(2)}KB, exceeding recommended limit of ${maxSizeKB}KB`);
        }
        
        localStorage.setItem(name, valueToStore);
        return true;
    } catch (error) {
        if (!silent) {
            console.error(`Failed to set localStorage item "${name}":`, error);
        }
        
        if (error.name === 'QuotaExceededError') {
            if (!silent) {
                console.warn('localStorage quota exceeded. Consider clearing old data or using IndexedDB for large datasets.');
            }
        }
        
        return false;
    }
};

export const getLocalStorage = (name, options = {}) => {
    const { decompress = true } = options;
    
    if (!isStorageAvailable()) {
        return null;
    }
    
    try {
        const value = localStorage.getItem(name);
        if (value === null) return null;
        
        // Decompress if needed (for now, just return as-is since we only remove whitespace)
        return decompress ? decompressJSON(value) : value;
    } catch (error) {
        console.error(`Failed to get localStorage item "${name}":`, error);
        return null;
    }
};

export const deleteLocalStorage = (name) => {
    if (!isStorageAvailable()) {
        return false;
    }
    
    try {
        localStorage.removeItem(name);
        return true;
    } catch (error) {
        console.error(`Failed to delete localStorage item "${name}":`, error);
        return false;
    }
};

// Get storage statistics
export const getStorageStats = (name) => {
    if (!isStorageAvailable()) {
        return null;
    }
    
    try {
        const sizeKB = getStorageSize(name);
        const value = localStorage.getItem(name);
        
        return {
            exists: value !== null,
            sizeKB: sizeKB.toFixed(2),
            sizeBytes: Math.round(sizeKB * 1024),
            isEmpty: !value || value.length === 0
        };
    } catch (error) {
        console.error(`Failed to get storage stats for "${name}":`, error);
        return null;
    }
};

// Check if storage is getting full
export const checkStorageHealth = () => {
    if (!isStorageAvailable()) {
        return { available: false, usage: null, warning: 'localStorage not available' };
    }
    
    try {
        let totalSize = 0;
        let itemCount = 0;
        
        for (let i = 0; i < localStorage.length; i++) {
            const key = localStorage.key(i);
            if (key) {
                const value = localStorage.getItem(key);
                if (value) {
                    totalSize += new Blob([value]).size;
                    itemCount++;
                }
            }
        }
        
        const totalSizeKB = totalSize / 1024;
        const totalSizeMB = totalSizeKB / 1024;
        
        // Most browsers have 5-10MB limit
        const limitMB = 5; // Conservative estimate
        const usagePercent = (totalSizeMB / limitMB) * 100;
        
        return {
            available: true,
            usage: {
                totalSizeKB: totalSizeKB.toFixed(2),
                totalSizeMB: totalSizeMB.toFixed(2),
                itemCount,
                usagePercent: usagePercent.toFixed(1),
                limitMB
            },
            warning: usagePercent > 80 ? 'Storage is getting full' : null
        };
    } catch (error) {
        return { available: false, usage: null, warning: error.message };
    }
};

